import FitCurveIcon from '@/assets/images/FitCurveIcon.svg';
import FitPrimeIcon from '@/assets/images/FitPrimeIcon.svg';
import FitVarsityIcon from '@/assets/images/FitVarsityIcon.svg';
import FitCurveImg from '@/assets/images/fitCurve.jpg';
import FitPrimeImg from '@/assets/images/fitPrime.jpg';
import FitVarsityImg from '@/assets/images/fitVarsity.jpg';
import HomeImg from '@/assets/images/home-img.jpg';
import playButtonIcon from '@/assets/images/playButton.svg';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { ChevronRight } from 'lucide-react';
import { useEffect, useState } from 'react';

interface ProgramSlide {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  backgroundImage?: string;
  icon?: React.ReactNode;
  showTrainingTypes?: boolean;
  buttons: {
    primary?: {
      text: string;
      onClick: () => void;
    };
    secondary?: {
      text: string;
      onClick: () => void;
    };
    tertiary?: {
      text: string;
      onClick: () => void;
    };
  };
}

interface ProgramSliderProps {
  onScheduleSession: (program?: string) => void;
  onHowItWorks: () => void;
  onPayAsYouGo: (program?: string) => void;
  loading?: boolean;
  hasExistingSessions?: boolean;
  sessionBalance?: {
    fitPrimeBalance?: number;
    fitCurveBalance?: number;
    fitVarsityBalance?: number;
    freeSessionBalance?: number;
  };
  trainingTypes?: Array<{
    __typename?: 'TrainingType' | undefined;
    _id: string;
    order: number;
    title: string;
    icon?: string | null | undefined;
  }>;
  showAll: boolean;
  onToggleShowAll: () => void;
}

export default function ProgramSlider({
  onScheduleSession,
  onHowItWorks,
  onPayAsYouGo,
  sessionBalance,
  loading = false,
  hasExistingSessions = false,
  trainingTypes = [],
  showAll,
  onToggleShowAll,
}: ProgramSliderProps) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [progress, setProgress] = useState(0);
  const [autoProgress, setAutoProgress] = useState(true);

  const visibleTrainingTypes = showAll ? trainingTypes || [] : trainingTypes?.slice(0, 5) || [];

  const handleProgramSelection = (programId: string) => {
    onScheduleSession(programId);
  };


  const slides: ProgramSlide[] = [
    {
      id: 'selection',
      title: 'Find the Right Program for You',
      subtitle: '',
      description:
        'Choose from our expert-designed fitness programs tailored to your goals and lifestyle.',
      backgroundImage: HomeImg,
      showTrainingTypes: false,
      buttons: {
        primary: {
          text: sessionBalance?.freeSessionBalance && sessionBalance?.freeSessionBalance > 0 ? 'Schedule Free Session' : 'Schedule New Session',
          onClick: () => onScheduleSession(),
        },
        secondary: {
          text: 'How it Works',
          onClick: onHowItWorks,
        },
        tertiary: {
          text: 'Pay as you Go',
          onClick: () => onPayAsYouGo(),
        },
      },
    },
    {
      id: 'fitprime',
      title: 'FitPrime',
      subtitle: '',
      description:
        '1-on-1 personal training. Choose your path — Yoga, Strength, Cardio & more. Pay as you go',
      backgroundImage: FitPrimeImg, // Will be replaced with actual FitPrime background
      icon: <img src={FitPrimeIcon} alt='FitPrime' className='w-8 h-8' />,
      showTrainingTypes: true,
      buttons: {
        primary: {
          text: hasExistingSessions ? 'Start Session' : sessionBalance?.freeSessionBalance && sessionBalance?.freeSessionBalance > 0 ? 'Schedule Free Session' : 'Schedule New Session',
          onClick: () => handleProgramSelection('fitprime'),
        },
        secondary: {
          text: 'How it Works',
          onClick: onHowItWorks,
        },
        tertiary: {
          text: 'Pay as you Go',
          onClick: () => onPayAsYouGo('fitprime'),
        },
      },
    },
    {
      id: 'fitcurve',
      title: 'FitCurve',
      subtitle: '',
      description:
        'Lower body sculpting program built for results. Stronger curves in just weeks. Transform your silhouette and boost your confidence with our tailored workouts.',
      backgroundImage: FitCurveImg, // Will be replaced with actual FitCurve background
      icon: <img src={FitCurveIcon} alt='FitCurve' className='w-8 h-8' />,
      showTrainingTypes: false,
      buttons: {
        primary: {
          text: sessionBalance?.freeSessionBalance && sessionBalance?.freeSessionBalance > 0 ? 'Schedule Free Session' : 'Schedule New Session',
          onClick: () => handleProgramSelection('fitcurve'),
        },
        secondary: {
          text: 'How it Works',
          onClick: onHowItWorks,
        },
        tertiary: {
          text: 'Pay as you Go',
          onClick: () => onPayAsYouGo('fitcurve'),
        },
      },
    },
    {
      id: 'fitvarsity',
      title: 'FitVarsity',
      subtitle: '',
      description:
        'Athletic training for ages 13–18. Sport-specific workouts to level up your game. Join us to enhance your skills and boost your confidence on the field.',
      backgroundImage: FitVarsityImg, // Will be replaced with actual FitVarsity background
      icon: <img src={FitVarsityIcon} alt='FitVarsity' className='w-8 h-8' />,
      showTrainingTypes: false,
      buttons: {
        primary: {
          text: sessionBalance?.freeSessionBalance && sessionBalance?.freeSessionBalance > 0 ? 'Schedule Free Session' : 'Schedule New Session',
          onClick: () => handleProgramSelection('fitvarsity'),
        },
        secondary: {
          text: 'How it Works',
          onClick: onHowItWorks,
        },
        tertiary: {
          text: 'Pay as you Go',
          onClick: () => onPayAsYouGo('fitvarsity'),
        },
      },
    },


  ];

  const handleManualSlideChange = (slideIndex: number) => {
    setCurrentSlide(slideIndex);
    setProgress(0);
    setAutoProgress(false);

    // Re-enable auto-progression after 10 seconds
    setTimeout(() => {
      setAutoProgress(true);
    }, 10000);
  };

  useEffect(() => {
    if (!autoProgress) return;

    const timer = setInterval(() => {
      setProgress((oldProgress) => {
        if (oldProgress === 100) {
          setCurrentSlide((prevSlide) => {
            const nextSlide = (prevSlide + 1) % slides.length;
            return nextSlide;
          });
          return 0;
        }
        return oldProgress + 1;
      });
    }, 100);

    return () => clearInterval(timer);
  }, [slides.length, autoProgress]);

  const currentSlideData = slides[currentSlide];
  const isFirstSlide = currentSlide === 0;
  const isFitPrimeSlide = currentSlideData.id === 'fitprime';

  return (
    <div className='relative overflow-hidden border-none rounded-xl border border-bgPrimary bg-bgSecondary'>
      <div
        className='w-full h-full z-10 absolute rounded-xl'
        style={{
          background:
            'linear-gradient(268.59deg, rgba(26, 26, 40, 0) 4.04%, rgba(26, 26, 40, 0.62) 28.07%, #1A1A28 51.09%)',
        }}
      />
      <img
        src={currentSlideData.backgroundImage}
        alt='Background'
        width={1200}
        height={400}
        className='absolute inset-0 object-contain object-right w-full h-full scale-150 -translate-x-60 translate-y-12 rounded-xl'
      />

      <div className='relative z-10 p-8 rounded-xl'>
        {/* Progress indicators */}
        {!isFirstSlide && (
          <div className='flex gap-1 mb-4'>
            {slides.slice(1).map((_, i) => (
              <Progress
                key={i}
                value={currentSlide - 1 === i ? progress : i < currentSlide - 1 ? 100 : 0}
                className='w-4 h-1 rounded-full'
              />
            ))}
          </div>
        )}

        {/* Program selector for first slide */}
        {isFirstSlide && (
          <div className='flex gap-1 mb-4'>
            <div className='w-4 h-1 bg-white rounded-full' />
            <div className='w-2.5 h-1 bg-white/20 rounded-full' />
            <div className='w-2.5 h-1 bg-white/20 rounded-full' />
            <div className='w-2.5 h-1 bg-white/20 rounded-full' />
          </div>
        )}

        {/* Title section */}
        <div className='mb-6'>
          {currentSlideData.icon && (
            <div className='flex items-center gap-2 mb-3'>
              <div>{currentSlideData.icon}</div>
              <h2 className='text-xl font-bold text-white'>{currentSlideData.title}</h2>
            </div>
          )}
          {!currentSlideData.icon && (
            <h2 className='text-2xl font-bold mb-4 text-white'>{currentSlideData.title}</h2>
          )}
          <p className='text-white text-sm leading-relaxed'>
            {currentSlideData.description.split('.').map((sentence, index, array) => {
              // Skip empty strings
              if (!sentence.trim()) return null;
              const isLast = index === array.length - 1;
              const isSecondSentence = index === 1;
              return (
                <span key={index}>
                  {sentence.trim()}{!isLast && '.'}
                  {isSecondSentence && <br />}
                  {!isSecondSentence && !isLast && ' '}
                </span>
              );
            })}
          </p>
        </div>

        {/* Program Selection Cards for first slide */}
        {isFirstSlide && (
          <div className='flex gap-2.5 mb-6'>
            <div
              className='flex flex-col gap-1.5 cursor-pointer flex-1 p-3  h-16'
              onClick={() => handleManualSlideChange(1)}
            >
              <div className='flex items-center gap-1'>
                <div>
                  <img src={FitPrimeIcon} alt='FitPrime' className='w-3.5 h-3.5' />
                </div>
                <span className='text-white text-xs font-semibold'>FitPrime</span>
              </div>
              <p className='text-[#C5C8D0] text-xs leading-tight'>
                Personal training just for you — Yoga, Strength, Cardio & more. Pay as you go.
              </p>
            </div>
            <div
              className='flex flex-col gap-1.5 cursor-pointer flex-1 p-3 border-l-2 border-white/30 h-16'
              onClick={() => handleManualSlideChange(2)}
            >
              <div className='flex items-center gap-1'>
                <div>
                  <img src={FitCurveIcon} alt='FitCurve' className='w-3.5 h-3.5' />
                </div>
                <span className='text-white text-xs font-semibold'>FitCurve</span>
              </div>
              <p className='text-[#C5C8D0] text-xs leading-tight'>
                Lower body sculpting for visible results. Achieve stronger curves in weeks.
              </p>
            </div>
            <div
              className='flex flex-col gap-1.5 cursor-pointer flex-1 p-3  border-l-2 border-white/30 h-16'
              onClick={() => handleManualSlideChange(3)}
            >
              <div className='flex items-center gap-1'>
                <div>
                  <img src={FitVarsityIcon} alt='FitVarsity' className='w-3.5 h-3.5' />
                </div>
                <span className='text-white text-xs font-semibold'>FitVarsity</span>
              </div>
              <p className='text-[#C5C8D0] text-xs leading-tight'>
                Training for ages 13–18 with sport-specific workouts to boost your game.
              </p>
            </div>




          </div>
        )}

        {/* Training types for FitPrime */}
        {isFitPrimeSlide && currentSlideData.showTrainingTypes && (
          <div className='flex flex-wrap gap-3 mb-6'>
            {visibleTrainingTypes.map((type) => (
              <div key={type._id} className='flex items-center bg-white/10 rounded-lg px-3 py-1'>
                {type.icon && <img src={type.icon} alt={type.title} className='mr-2 h-5 w-5' />}
                <span className='text-white text-sm font-medium'>{type.title}</span>
              </div>
            ))}
            {!showAll && (trainingTypes?.length || 0) > 5 && (
              <button
                className='flex items-center text-[#0094FF] text-sm cursor-pointer'
                onClick={onToggleShowAll}
              >
                And more
              </button>
            )}
            {showAll && (
              <button
                className='flex items-center text-[#0094FF] text-sm cursor-pointer'
                onClick={onToggleShowAll}
              >
                Show less
              </button>
            )}
          </div>
        )}

        {/* Buttons */}
        {loading ? (
          <div className='flex flex-row gap-4'>
            <div className='w-56 h-10 bg-gray-300 animate-pulse rounded-md' />
            <div className='w-56 h-10 bg-gray-300 animate-pulse rounded-md' />
            <div className='w-56 h-10 bg-gray-300 animate-pulse rounded-md' />
          </div>
        ) : (
          <div className='flex flex-row gap-4'>
            {currentSlideData.buttons.primary && (
              <Button
                id='program-primary-btn'
                size='sm'
                variant='default'
                className='hidden text-black sm:flex items-center justify-center px-5 py-2 w-56 h-auto'
                onClick={currentSlideData.buttons.primary.onClick}
              >
                {currentSlideData.buttons.primary.text}
              </Button>
            )}

            {currentSlideData.buttons.secondary && (
              <Button
                id='program-secondary-btn'
                size='sm'
                variant='ghost'
                className='hidden text-white bg-[#343840] font-medium sm:flex items-center justify-center px-5 py-2 w-56 h-auto'
                onClick={currentSlideData.buttons.secondary.onClick}
              >
                <img src={playButtonIcon} height={24} width={24} className='mr-2' />
                {currentSlideData.buttons.secondary.text}
              </Button>
            )}

            {currentSlideData.buttons.tertiary && (
              <Button
                id='program-tertiary-btn'
                size='sm'
                variant='destructive'
                className='hidden text-white bg-transparent hover:bg-transparent hover:text-gray-400 font-medium sm:flex items-center justify-center px-5 py-2 w-56 h-auto'
                onClick={currentSlideData.buttons.tertiary.onClick}
              >
                {currentSlideData.buttons.tertiary.text} <ChevronRight />
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
